import React from 'react';
import { titleize, humanize, unescapeHTML } from 'underscore.string';
import moment from 'moment';
import { Button, BadgeAlt, Progress } from '@cargomatic/ui';
import { Calendar, Pencil, Trash2 } from 'lucide-react';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import ShipmentStatusBadge from 'components/v2/ShipmentStatusBadge';
import ShipperStatusBadge from 'components/v2/ShipperStatusBadge';
import AccessorialRequestStatusBadge from 'components/v2/AccessorialRequestStatusBadge';
import ExternalLink from 'components/v2/ExternalLink';
import AppointmentConfirmationBadge from 'components/modules/shipments/stops/AppointmentConfirmationBadge';
import { BadgedOverflowCell } from './BadgedOverflowCell';
import { LoadUnitFormatter, LoadDimensionFormatter, } from 'components/modules/master/formatters';
import { getLocationTimezone } from 'helpers/locations';
import { getPickupStop, getDeliverStop } from 'helpers/stops';
import { canEditAppointmentWindow } from 'helpers/shipments';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { getTimeZoneName } from 'utils/Timezones';
import { mapTags } from 'utils/formatters/tags';
import { DocumentsCell } from './DocumentsCell';
import accounting from 'accounting';
import _ from 'underscore';
import { decode } from 'he';
import i18next from 'i18next';
// ---------------------------------------------------------------------------
// Simple value formatters
// ---------------------------------------------------------------------------
export const currencyFormatter = (cell, row) => React.createElement("span", null, formatMoney(cell, row === null || row === void 0 ? void 0 : row.currency));
export const priceFormatter = (cell) => React.createElement("span", null,
    "$",
    String(cell));
export const genericFormatter = (cell) => React.createElement("span", null, String(cell));
export const dateFormatter = (cell) => (React.createElement("span", null, cell ? moment(cell).format('L') : ''));
export const dateTimeFormatter = (cell) => (React.createElement("span", null, cell ? moment(cell).format('L LT') : ''));
export const defaultFormatter = (cell) => {
    if (cell === null || cell === undefined)
        return null;
    return React.createElement("span", null, titleize(humanize(unescapeHTML(String(cell)))));
};
export const booleanFormatter = (cell) => {
    const label = cell === true ? i18next.t('YES') : i18next.t('NO');
    return React.createElement("span", null, label);
};
export const masterAccountFormatter = (cell) => {
    if (!cell)
        return 'NA';
    return String(cell);
};
export const credentialFormatter = (_cell, row) => {
    if (row._type === 'Driver')
        return row.username;
    return row.email;
};
export const objectIdTimestampFormatter = (_cell, row) => {
    const dateFromObjectId = (objectId) => new Date(parseInt(objectId.substring(0, 8), 16) * 1000);
    return (React.createElement("span", null, moment(dateFromObjectId(row._id)).format('YYYY-MM-DD')));
};
// ---------------------------------------------------------------------------
// Badge / status formatters
// ---------------------------------------------------------------------------
export const statusFormatter = (cell) => (React.createElement(ShipmentStatusBadge, { status: cell }));
export const accessorialStatusFormatter = (cell) => (React.createElement(AccessorialRequestStatusBadge, { status: cell }));
export const serviceWatchFormatter = (cell) => (React.createElement(ShipperStatusBadge, { status: cell }));
// ---------------------------------------------------------------------------
// Cost formatters
// ---------------------------------------------------------------------------
export const shipperCostFormatter = (cell, row) => {
    const receivables = row.receivables || [];
    if (!receivables.length) {
        return React.createElement("span", null, formatMoney(cell, row === null || row === void 0 ? void 0 : row.currency));
    }
    const recTotal = receivables.reduce((total, item) => total + ((item === null || item === void 0 ? void 0 : item.value) || 0), 0);
    return React.createElement("span", null, formatMoney(recTotal, row === null || row === void 0 ? void 0 : row.currency));
};
export const carrierPayoutFormatter = (cell, row) => {
    const payables = row.payables || [];
    if (!payables.length) {
        return React.createElement("span", null, formatMoney(cell, row === null || row === void 0 ? void 0 : row.currency));
    }
    const payableTotal = payables.reduce((total, item) => total + ((item === null || item === void 0 ? void 0 : item.value) || 0), 0);
    return React.createElement("span", null, formatMoney(payableTotal, row === null || row === void 0 ? void 0 : row.currency));
};
// ---------------------------------------------------------------------------
// Complex formatters
// ---------------------------------------------------------------------------
export const exceptionReporterFormatter = (cell) => {
    if (cell === 'cargomatic-automation') {
        return (React.createElement("img", { alt: "cargomatic-automation", src: "/img/storm-trooper.jpg", className: "icon", width: "25" }));
    }
    return React.createElement("span", null, String(cell));
};
export const rolesFormatter = (cell, row) => {
    if (!cell)
        return null;
    const roles = _.uniq(row.roles);
    return (React.createElement("div", { className: "flex flex-wrap gap-1" }, roles.map((role) => (React.createElement(BadgeAlt, { key: role, variant: "secondary" }, i18next.t(sluggify(role)))))));
};
export const tagsFormatter = (_cell, row) => {
    if (!row.tags)
        return null;
    const tags = mapTags(row.tags);
    return (React.createElement("div", { className: "flex flex-col gap-1" }, tags.map((tag, i) => (React.createElement(BadgeAlt, { key: `${tag}-${i}`, variant: "outline" }, tag)))));
};
export const locationFormatter = (cell) => {
    if (!cell)
        return null;
    const loc = cell;
    const name = loc.name ? String(loc.name) : '';
    return (React.createElement("div", null,
        React.createElement("span", { className: "mr-2 text-sm font-semibold text-muted-foreground" }, humanize(decode(name))),
        React.createElement("span", { className: "mb-4" }, String(loc.address || ''))));
};
const equipmentRefTypes = new Set([
    'chassis_number',
    'equipment_id',
    'trailer_number',
]);
const EQUIPMENT_BADGE = {
    equipment_id: {
        label: 'Container',
        className: 'bg-blue-100 text-blue-800 border-blue-300',
    },
    chassis_number: {
        label: 'Chassis',
        className: 'bg-purple-100 text-purple-800 border-purple-300',
    },
    trailer_number: {
        label: 'Trailer',
        className: 'bg-gray-100 text-gray-800 border-gray-300',
    },
};
export const equipmentListFormatter = (cell) => {
    const items = _.uniq(cell || [], (item) => item.value);
    const refs = items.filter((r) => !r.hidden && equipmentRefTypes.has(r.name));
    const badged = refs.map((refNum) => {
        const name = refNum.name;
        const meta = EQUIPMENT_BADGE[name];
        return {
            key: `${String(refNum.name)}-${String(refNum.value)}`,
            value: String(refNum.value),
            badgeLabel: meta === null || meta === void 0 ? void 0 : meta.label,
            badgeClassName: meta === null || meta === void 0 ? void 0 : meta.className,
        };
    });
    return React.createElement(BadgedOverflowCell, { items: badged, title: "Equipment" });
};
const REFERENCE_FIELDS = {
    partnerShipmentId: 'Partner Shipment',
    work_order_number: 'Work Order Number',
    house_bill_number: 'House Bill',
    mg_load_id: 'MG Load ID',
    mo_number: 'MO Number',
    tracking_id: 'Tracking ID',
    seal_number: 'Seal #',
    delivery_order: 'Delivery Order',
    master_bol: 'Master Reference',
    pu_number: 'PU Number',
};
const REFERENCE_BADGE_CLASSES = {
    partnerShipmentId: 'bg-sky-100 text-sky-800 border-sky-300',
    work_order_number: 'bg-amber-100 text-amber-800 border-amber-300',
    house_bill_number: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    mg_load_id: 'bg-teal-100 text-teal-800 border-teal-300',
    mo_number: 'bg-rose-100 text-rose-800 border-rose-300',
    tracking_id: 'bg-blue-100 text-blue-800 border-blue-300',
    seal_number: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    delivery_order: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    master_bol: 'bg-violet-100 text-violet-800 border-violet-300',
    pu_number: 'bg-pink-100 text-pink-800 border-pink-300',
};
export const referenceNumberFormatter = (cell) => {
    const items = _.uniq(cell || [], (item) => item.value);
    const refs = items.filter((r) => !r.hidden && Object.keys(REFERENCE_FIELDS).indexOf(r.name) > -1);
    const badged = refs.map((refNum) => {
        const name = refNum.name;
        const label = i18next.t(sluggify(REFERENCE_FIELDS[name]));
        return {
            key: `${name}-${String(refNum.value)}`,
            value: String(refNum.value),
            badgeLabel: label,
            badgeClassName: REFERENCE_BADGE_CLASSES[name],
        };
    });
    return React.createElement(BadgedOverflowCell, { items: badged, title: "Reference Numbers" });
};
export const customerFormatter = (cell, row) => {
    var _a;
    let entityId = row._id;
    let field = row;
    if (cell && typeof cell === 'object') {
        field = cell;
        entityId = field._id;
    }
    const showPortProIcon = field._type === 'Carrier' &&
        'portpro' in field &&
        ((_a = field === null || field === void 0 ? void 0 : field.portpro) === null || _a === void 0 ? void 0 : _a.enabled);
    let customer;
    switch (field._type) {
        case 'Shipper':
            customer = unescapeHTML(`${field.company_name || ''}`);
            break;
        case 'Carrier':
            customer = unescapeHTML(`${field.name || ''}`);
            break;
        default:
            customer = `${field.first_name || ''} ${field.last_name || ''}`;
    }
    // V1 falls back to row-level fields when `cell` is absent, so only the
    // resolved display string decides whether to render — not `cell` itself.
    const hasValue = !!customer.trim();
    if (!hasValue)
        return null;
    return (React.createElement("div", { className: "flex items-center gap-1" },
        React.createElement(ExternalLink, { title: titleize(humanize(decode(customer))), href: `/users/${entityId}`, 
            // V1 renders a plain link with no icon; ExternalLink defaults
            // showIcon to true, so opt out explicitly for parity.
            showIcon: false, dataTestId: `customer-${entityId}` }),
        showPortProIcon && (React.createElement("img", { src: "/img/icons/portProLogo.png", className: "w-5 shrink-0", title: i18next.t('CARRIER_INTEGRATED_WITH_PORTPRO'), alt: i18next.t('CARRIER_INTEGRATED_WITH_PORTPRO') }))));
};
// ---------------------------------------------------------------------------
// Datetime range formatter
// ---------------------------------------------------------------------------
function renderDatetimeRange(startStr, endStr, timezone) {
    if (!startStr || !endStr) {
        return (React.createElement("div", { className: "whitespace-nowrap" }, i18next.t('NEEDS_APPOINTMENT')));
    }
    const start = moment(startStr);
    const end = endStr ? moment(endStr) : undefined;
    const startWithTz = timezone ? start.clone().tz(timezone) : start.clone();
    const endWithTz = end && timezone ? end.clone().tz(timezone) : end === null || end === void 0 ? void 0 : end.clone();
    const formattedTimezone = timezone ? getTimeZoneName(timezone) : '';
    const startDate = startWithTz.format('L');
    const startTime = startWithTz.format('LT');
    if (endWithTz && startWithTz.isSame(endWithTz)) {
        return (React.createElement("div", { className: "whitespace-nowrap" },
            startDate,
            React.createElement("br", null),
            startTime,
            " ",
            formattedTimezone));
    }
    if (endWithTz && startWithTz.isSame(endWithTz, 'day')) {
        const endTime = endWithTz === null || endWithTz === void 0 ? void 0 : endWithTz.format('LT');
        return (React.createElement("div", { className: "whitespace-nowrap" },
            startDate,
            React.createElement("br", null),
            startTime,
            " - ",
            endTime,
            ' ',
            React.createElement("span", { className: "font-semibold text-emerald-500" }, formattedTimezone)));
    }
    const endDate = endWithTz === null || endWithTz === void 0 ? void 0 : endWithTz.format('L');
    const endTime = endWithTz === null || endWithTz === void 0 ? void 0 : endWithTz.format('LT');
    return (React.createElement("div", { className: "whitespace-nowrap" },
        React.createElement("div", null,
            startDate,
            React.createElement("br", null),
            startTime),
        React.createElement("span", null, "-"),
        React.createElement("div", null,
            endDate,
            React.createElement("br", null),
            endTime,
            ' ',
            React.createElement("span", { className: "font-semibold text-emerald-500" }, formattedTimezone))));
}
export const datetimeRangeFormatter = (_cell, row, _rowIndex, extra) => {
    var _a;
    let timezone = ((_a = row.market) === null || _a === void 0 ? void 0 : _a.timezone) ||
        'America/Los_Angeles';
    let start = row.pickup_after;
    let end = row.pickup_by;
    let stop;
    let isAdmin = false;
    if (extra) {
        if (extra === null || extra === void 0 ? void 0 : extra.admin)
            isAdmin = true;
        if ((extra === null || extra === void 0 ? void 0 : extra.type) === 'deliver') {
            start = row.deliver_after;
            end = row.deliver_by;
            timezone = getLocationTimezone(row.destination) || timezone;
            stop = getDeliverStop(row);
        }
        else {
            timezone = getLocationTimezone(row.origin) || timezone;
            stop = getPickupStop(row);
        }
    }
    let canEditWindow = canEditAppointmentWindow(row, isAdmin);
    if ((extra === null || extra === void 0 ? void 0 : extra.isCarrier) && !isIntermodalShipmentType(row)) {
        canEditWindow = false;
    }
    const confirmed = !(stop || {})
        .appointment_unconfirmed;
    return (React.createElement("div", { className: "flex flex-col items-start gap-1" },
        renderDatetimeRange(start, end, timezone),
        row.shipment_type === 'drayage' && (React.createElement(AppointmentConfirmationBadge, { confirmed: confirmed })),
        canEditWindow &&
            (extra === null || extra === void 0 ? void 0 : extra.onWindowEdit) &&
            typeof extra.onWindowEdit === 'function' && (React.createElement("div", { className: "mt-4" },
            React.createElement(Button, { variant: "ghost", className: "h-8 px-2 text-sm text-emerald-600 hover:text-emerald-700", onClick: () => extra.onWindowEdit(extra === null || extra === void 0 ? void 0 : extra.type, row) },
                React.createElement(Calendar, { className: "mr-1 h-4 w-4" }),
                React.createElement("span", null, i18next.t('EDIT')))))));
};
// ---------------------------------------------------------------------------
// SLA formatter
// ---------------------------------------------------------------------------
function getProgressColor(actual, expected) {
    const diff = Math.abs(actual - expected);
    if (diff >= 30)
        return '[&>div]:bg-red-500';
    if (diff >= 20)
        return '[&>div]:bg-yellow-500';
    return '[&>div]:bg-green-500';
}
export const slaFormatter = (_cell, row) => {
    const actual = row.actual_progress_percentage;
    const expected = row.expected_progress_percentage;
    const actualColor = getProgressColor(actual, expected);
    const expectedColor = getProgressColor(expected, expected);
    return (React.createElement("div", { className: "min-w-[120px]" },
        React.createElement("div", { className: "flex items-center justify-between mb-1" },
            React.createElement("span", { className: "text-xs font-bold text-muted-foreground" }, i18next.t('ACTUAL')),
            React.createElement("span", { className: "text-xs text-muted-foreground" },
                actual,
                "%")),
        React.createElement(Progress, { value: actual, className: `h-2 mb-2 ${actualColor}` }),
        React.createElement("div", { className: "flex items-center justify-between mb-1" },
            React.createElement("span", { className: "text-xs font-bold text-muted-foreground" }, i18next.t('EXPECTED')),
            React.createElement("span", { className: "text-xs text-muted-foreground" },
                expected,
                "%")),
        React.createElement(Progress, { value: expected, className: `h-2 ${expectedColor}` })));
};
// ---------------------------------------------------------------------------
// Load details formatter
// ---------------------------------------------------------------------------
export const loadDetailsFormatter = (_cell, row) => {
    const rows = [];
    if (row.full_truckload) {
        rows.push(React.createElement("div", { key: "full-truckload-cell" }, "Truckload"));
    }
    const loads = row.loads || [];
    loads.forEach((l, i) => {
        rows.push(React.createElement("div", { key: i },
            React.createElement(LoadDimensionFormatter, { quantity: l.quantity, type: l.type, length: l.length, width: l.width, height: l.height, dimensional_units: l.dimensional_units })));
    });
    rows.push(React.createElement("div", { key: "load-weight" },
        React.createElement(LoadUnitFormatter, { weight: accounting.format(row.weight), weight_units: row.weight_units })));
    return React.createElement("div", { className: "whitespace-nowrap" }, rows);
};
// ---------------------------------------------------------------------------
// Action formatters
// ---------------------------------------------------------------------------
export const customActionsFormatter = (extraProps) => function CustomActionsCell(_cell, row) {
    if (!extraProps)
        return null;
    return (React.createElement("div", { className: "flex flex-wrap items-center gap-1" },
        extraProps.canEdit &&
            extraProps.onEdit &&
            typeof extraProps.onEdit === 'function' && (React.createElement(Button, { variant: "ghost", size: "icon", className: "h-8 w-8", "aria-label": "Edit", onClick: (e) => {
                e.stopPropagation();
                extraProps.onEdit(row);
            } },
            React.createElement(Pencil, { className: "h-4 w-4" }))),
        extraProps.canDelete &&
            extraProps.onDelete &&
            typeof extraProps.onDelete === 'function' && (React.createElement(Button, { variant: "ghost", size: "icon", className: "h-8 w-8", "aria-label": "Delete", onClick: (e) => {
                e.stopPropagation();
                extraProps.onDelete(row);
            } },
            React.createElement(Trash2, { className: "h-4 w-4" })))));
};
// ---------------------------------------------------------------------------
// Entity / resource link formatters
// ---------------------------------------------------------------------------
export const createResourceFormatter = (resource) => function ResourceCell(cell, row, _rowIndex, extra) {
    const subEntityField = extra && (extra === null || extra === void 0 ? void 0 : extra.attribute) ? extra.attribute : null;
    let entityId = typeof cell === 'object'
        ? (cell || {})._id
        : row._id;
    const isCreditMemo = row.credit_memos &&
        row.credit_memos.length &&
        cell === row.credit_memos[0].number;
    if (resource === 'invoices' && isCreditMemo) {
        entityId = row.credit_memos[0]._id;
    }
    if (extra && (extra === null || extra === void 0 ? void 0 : extra.entityIdField)) {
        entityId = row[extra.entityIdField];
    }
    if (!(entityId || (extra && (extra === null || extra === void 0 ? void 0 : extra.onClick))))
        return null;
    const field = cell && subEntityField
        ? cell[subEntityField]
        : cell;
    const link = `/${resource}/${entityId}`;
    if (extra && typeof (extra === null || extra === void 0 ? void 0 : extra.onClick) === 'function') {
        return (React.createElement("div", { className: "flex flex-col items-center justify-center" },
            React.createElement("div", { className: "block overflow-hidden truncate mb-2" },
                React.createElement("a", { className: "text-primary hover:underline", title: String(field), href: "#", onClick: (e) => {
                        e.preventDefault();
                        extra.onClick(row, e);
                    } }, String(field))),
            (extra === null || extra === void 0 ? void 0 : extra.showPreNoticeBadge) && showPreNoticeBadge(row)));
    }
    if (extra && (extra === null || extra === void 0 ? void 0 : extra.linkTarget)) {
        return (React.createElement("div", { className: "flex flex-col items-center justify-center" },
            React.createElement("div", { className: "block overflow-hidden truncate mb-2" },
                React.createElement("a", { className: "text-primary hover:underline", title: String(field), href: link, target: extra.linkTarget, rel: "noreferrer noopener" }, String(field))),
            (extra === null || extra === void 0 ? void 0 : extra.showPreNoticeBadge) && showPreNoticeBadge(row)));
    }
    return (React.createElement("div", { className: "flex flex-col items-center justify-center" },
        React.createElement("div", { className: "block overflow-hidden truncate" },
            React.createElement(ExternalLink, { title: String(field), href: link, showIcon: !!field, dataTestId: `resource-${entityId}` })),
        (extra === null || extra === void 0 ? void 0 : extra.showPreNoticeBadge) && showPreNoticeBadge(row)));
};
export const getEntityFormatter = (type) => {
    switch (type) {
        case 'location':
            return createResourceFormatter('locations');
        case 'order':
            return createResourceFormatter('orders');
        case 'shipment':
            return createResourceFormatter('shipments');
        case 'customer':
            return createResourceFormatter('users');
        case 'invoice':
            return createResourceFormatter('invoices');
        case 'payout':
            return createResourceFormatter('payouts');
        default: {
            const EntityFallback = (_cell) => (React.createElement("div", { className: "block overflow-hidden truncate" },
                React.createElement("span", { className: "text-primary" }, String(_cell))));
            return EntityFallback;
        }
    }
};
// ---------------------------------------------------------------------------
// Invoice / documents formatters
// ---------------------------------------------------------------------------
export const InvoiceFormatter = (cell) => {
    const invoices = cell;
    let invoiceSent = 'N/A';
    if (invoices && invoices.length) {
        invoiceSent = 'No';
        if (invoices.every((inv) => inv.email_sent === true)) {
            invoiceSent = 'Yes';
        }
    }
    return React.createElement("div", null, invoiceSent);
};
export const documentsFormatter = (_cell, row) => {
    return (React.createElement(DocumentsCell, { shipment: row }));
};
// ---------------------------------------------------------------------------
// Pre-notice badge helper
// ---------------------------------------------------------------------------
function showPreNoticeBadge(row) {
    if (!row.pre_arrival ||
        !featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL))
        return null;
    return (React.createElement(BadgeAlt, { variant: "destructive" }, i18next.t(sluggify('PRE_NOTICE'))));
}
// ---------------------------------------------------------------------------
// Formatter registry (same switch as existing DataList)
// ---------------------------------------------------------------------------
export function getFormatterForType(column) {
    const formatter = resolveFormatterForType(column);
    // Legacy mapping formatters can return `undefined` for empty cells, and
    // TanStack treats a bare `undefined` from a cell renderer as a missing
    // render ("Nothing was returned from render"). Guarantee `null` instead so
    // every module's columns render empty cells safely.
    return (cell, row, rowIndex, extra) => { var _a; return (_a = formatter(cell, row, rowIndex, extra)) !== null && _a !== void 0 ? _a : null; };
}
function resolveFormatterForType(column) {
    if (column.formatter)
        return column.formatter;
    switch (column.type) {
        case 'generic':
            return genericFormatter;
        case 'currency':
            return currencyFormatter;
        case 'price':
            return priceFormatter;
        case 'entity':
            return getEntityFormatter(column.entityType || '');
        case 'customer':
            return customerFormatter;
        case 'credentials':
            return credentialFormatter;
        case 'master_account':
            return masterAccountFormatter;
        case 'service_watch':
            return serviceWatchFormatter;
        case 'loaddetails':
            return loadDetailsFormatter;
        case 'dateWithTime':
            return dateTimeFormatter;
        case 'datetime':
            return dateFormatter;
        case 'reference_number':
            return referenceNumberFormatter;
        case 'equipments':
            return equipmentListFormatter;
        case 'location':
            return locationFormatter;
        case 'status':
            return statusFormatter;
        case 'accessorial_status':
            return accessorialStatusFormatter;
        case 'objectId':
            return objectIdTimestampFormatter;
        case 'customActions':
            return customActionsFormatter(column.formatExtraData);
        case 'tags':
            return tagsFormatter;
        case 'reporter':
            return exceptionReporterFormatter;
        case 'roles':
            return rolesFormatter;
        case 'sla':
            return slaFormatter;
        case 'boolean':
            return booleanFormatter;
        case 'daterange':
            return datetimeRangeFormatter;
        case 'invoice':
            return InvoiceFormatter;
        case 'documents':
            return documentsFormatter;
        case 'shipper_cost':
            return shipperCostFormatter;
        case 'carrier_payout':
            return carrierPayoutFormatter;
        case 'raw':
            return (cell) => cell;
        default:
            return defaultFormatter;
    }
}
